// Considered: existing publisher danger patterns — no equivalent organism exists for channel lifecycle management.
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import {
  archiveChannel,
  unarchiveChannel,
  deleteChannel,
  transferChannel,
} from '../../../api/marketplaceApi';
import ConfirmDialogAtom, { confirmDialogAtom } from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import type { Channel } from 'types/channel';
import './ChannelDangerSectionOrganism.scss';

interface ChannelPermissions {
  canEditDetails: boolean;
  canManageCollaborators: boolean;
  canPublish: boolean;
  canDelete: boolean;
}

export interface ChannelDangerSectionOrganismProps {
  channel: Channel;
  channelId: string;
  permissions: ChannelPermissions;
  onChannelUpdated: () => void;
}

const ChannelDangerSectionOrganism: React.FC<ChannelDangerSectionOrganismProps> = ({
  channel,
  channelId,
  permissions,
  onChannelUpdated,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  const [transferTarget, setTransferTarget] = useState('');
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState<'archive' | 'unarchive' | 'delete' | 'transfer' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const isArchived = !!channel.is_archived;

  const handleArchive = useCallback(() => {
    confirmDialogAtom({
      variant: 'warning',
      header: t('marketplace.channelManage.v3Danger.archive.confirmTitle'),
      message: t('marketplace.channelManage.v3Danger.archive.confirmMessage'),
      acceptLabel: t('marketplace.channelManage.v3Danger.archive.confirmAccept'),
      rejectLabel: t('common.cancel'),
      accept: async () => {
        if (!apiClients) return;
        setActionLoading('archive');
        setActionError(null);
        try {
          await archiveChannel(apiClients.schemaClient, channelId);
          onChannelUpdated();
        } catch {
          setActionError(t('marketplace.channelManage.v3Danger.archive.error'));
        } finally {
          setActionLoading(null);
        }
      },
    });
  }, [apiClients, channelId, onChannelUpdated, t]);

  const handleUnarchive = useCallback(() => {
    confirmDialogAtom({
      variant: 'default',
      header: t('marketplace.channelManage.v3Danger.unarchive.confirmTitle'),
      message: t('marketplace.channelManage.v3Danger.unarchive.confirmMessage'),
      acceptLabel: t('marketplace.channelManage.v3Danger.unarchive.confirmAccept'),
      rejectLabel: t('common.cancel'),
      accept: async () => {
        if (!apiClients) return;
        setActionLoading('unarchive');
        setActionError(null);
        try {
          await unarchiveChannel(apiClients.schemaClient, channelId);
          onChannelUpdated();
        } catch {
          setActionError(t('marketplace.channelManage.v3Danger.unarchive.error'));
        } finally {
          setActionLoading(null);
        }
      },
    });
  }, [apiClients, channelId, onChannelUpdated, t]);

  const handleDelete = useCallback(() => {
    confirmDialogAtom({
      variant: 'danger',
      header: t('marketplace.channelManage.v3Danger.delete.confirmTitle'),
      message: t('marketplace.channelManage.v3Danger.delete.confirmMessage'),
      acceptLabel: t('marketplace.channelManage.v3Danger.delete.confirmAccept'),
      rejectLabel: t('common.cancel'),
      accept: async () => {
        if (!apiClients) return;
        setActionLoading('delete');
        setActionError(null);
        try {
          await deleteChannel(apiClients.schemaClient, channelId);
          onChannelUpdated();
        } catch {
          setActionError(t('marketplace.channelManage.v3Danger.delete.error'));
        } finally {
          setActionLoading(null);
        }
      },
    });
  }, [apiClients, channelId, onChannelUpdated, t]);

  const handleTransferSubmit = useCallback(async () => {
    if (!apiClients || !transferTarget.trim()) return;
    setActionLoading('transfer');
    setActionError(null);
    try {
      await transferChannel(apiClients.schemaClient, channel.system_name, {
        newPublisherSystemName: transferTarget.trim(),
      });
      setShowTransferDialog(false);
      setTransferTarget('');
      onChannelUpdated();
    } catch {
      setActionError(t('marketplace.channelManage.v3Danger.transfer.error'));
    } finally {
      setActionLoading(null);
    }
  }, [apiClients, channel.system_name, transferTarget, onChannelUpdated, t]);

  return (
    <section className="channel-danger-section" aria-label={t('marketplace.channelManage.v3Danger.title')}>
      <ConfirmDialogAtom />

      {actionError && (
        <div className="channel-danger-section__error" role="alert">
          {actionError}
        </div>
      )}

      {/* Transfer — metasphere only */}
      {channel.scope === 'metasphere' && (
        <div className="channel-danger-section__card">
          <div className="channel-danger-section__card-body">
            <h2 className="channel-danger-section__card-title">
              {t('marketplace.channelManage.v3Danger.transfer.title')}
            </h2>
            <p className="channel-danger-section__card-description">
              {t('marketplace.channelManage.v3Danger.transfer.description')}
            </p>
            <ButtonAtom
              label={t('marketplace.channelManage.v3Danger.transfer.action')}
              icon="pi pi-arrow-right-arrow-left"
              variant="secondary"
              className="channel-danger-section__btn-transfer"
              onClick={() => setShowTransferDialog(true)}
            />
          </div>
        </div>
      )}

      {/* Transfer dialog */}
      {showTransferDialog && (
        <div className="channel-danger-section__dialog-overlay" role="dialog" aria-modal="true">
          <div className="channel-danger-section__dialog">
            <h3 className="channel-danger-section__dialog-title">
              {t('marketplace.channelManage.v3Danger.transfer.dialogTitle')}
            </h3>
            <p className="channel-danger-section__dialog-description">
              {t('marketplace.channelManage.v3Danger.transfer.dialogDescription')}
            </p>
            <input
              className="channel-danger-section__dialog-input"
              type="text"
              value={transferTarget}
              onChange={(e) => setTransferTarget(e.target.value)}
              placeholder={t('marketplace.channelManage.v3Danger.transfer.placeholder')}
              aria-label={t('marketplace.channelManage.v3Danger.transfer.placeholder')}
            />
            <div className="channel-danger-section__dialog-actions">
              <ButtonAtom
                label={t('common.cancel')}
                variant="secondary"
                onClick={() => { setShowTransferDialog(false); setTransferTarget(''); }}
              />
              <ButtonAtom
                label={t('marketplace.channelManage.v3Danger.transfer.confirmAccept')}
                icon={actionLoading === 'transfer' ? 'pi pi-spin pi-spinner' : undefined}
                disabled={!transferTarget.trim() || actionLoading === 'transfer'}
                onClick={handleTransferSubmit}
              />
            </div>
          </div>
        </div>
      )}

      {/* Archive / Unarchive — owner only */}
      {permissions.canDelete && (
        <div className="channel-danger-section__card">
          <div className="channel-danger-section__card-body">
            <h2 className="channel-danger-section__card-title">
              {isArchived
                ? t('marketplace.channelManage.v3Danger.unarchive.title')
                : t('marketplace.channelManage.v3Danger.archive.title')}
            </h2>
            <p className="channel-danger-section__card-description">
              {isArchived
                ? t('marketplace.channelManage.v3Danger.unarchive.description')
                : t('marketplace.channelManage.v3Danger.archive.description')}
            </p>
            <ButtonAtom
              label={
                isArchived
                  ? t('marketplace.channelManage.v3Danger.unarchive.action')
                  : t('marketplace.channelManage.v3Danger.archive.action')
              }
              icon={actionLoading === 'archive' || actionLoading === 'unarchive' ? 'pi pi-spin pi-spinner' : 'pi pi-box'}
              variant="secondary"
              className={isArchived ? 'channel-danger-section__btn-transfer' : 'channel-danger-section__btn-archive'}
              disabled={actionLoading === 'archive' || actionLoading === 'unarchive'}
              onClick={isArchived ? handleUnarchive : handleArchive}
            />
          </div>
        </div>
      )}

      {/* Delete — owner only */}
      {permissions.canDelete && (
        <div className="channel-danger-section__card channel-danger-section__card--destructive">
          <div className="channel-danger-section__card-body">
            <h2 className="channel-danger-section__card-title">
              {t('marketplace.channelManage.v3Danger.delete.title')}
            </h2>
            <p className="channel-danger-section__card-description">
              {t('marketplace.channelManage.v3Danger.delete.description')}
            </p>
            <ButtonAtom
              label={t('marketplace.channelManage.v3Danger.delete.action')}
              icon={actionLoading === 'delete' ? 'pi pi-spin pi-spinner' : 'pi pi-trash'}
              variant="danger"
              className="channel-danger-section__btn-delete"
              disabled={actionLoading === 'delete'}
              onClick={handleDelete}
            />
          </div>
        </div>
      )}
    </section>
  );
};

ChannelDangerSectionOrganism.displayName = 'ChannelDangerSectionOrganism';

export default ChannelDangerSectionOrganism;
